import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Gavel, Coins, MapPin, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [bids, setBids] = useState([]);
  const [wins, setWins] = useState([]);
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/me/bids"),
      api.get("/me/wins"),
      api.get("/me/shipping"),
    ]).then(([b, w, s]) => {
      setBids(b.data.bids);
      setWins(w.data.wins);
      setShipping(s.data.shipping_address);
    });
  }, []);

  if (!user || typeof user !== "object") return null;

  const hasAddress = !!shipping;
  const needsAddress = wins.length > 0 && !hasAddress;

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mb-2">
          Hey, {user.name}
        </h1>
        <p className="text-zinc-500 mb-10">Your bidding activity at a glance.</p>

        {needsAddress && (
          <div
            data-testid="shipping-cta-banner"
            className="card-surface p-5 mb-6 flex items-center gap-4 border-[#FBBF24]/40"
            style={{ borderColor: "rgba(251,191,36,0.4)" }}
          >
            <AlertTriangle className="text-[#FBBF24] shrink-0" size={22} />
            <div className="flex-1">
              <div className="font-semibold">Your win is waiting on a shipping address.</div>
              <div className="text-sm text-zinc-400">
                Add where to send your prize so we can ship it.
              </div>
            </div>
            <Link to="/shipping" className="btn-bid !py-2 !px-4 text-xs">
              Add address →
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card icon={<Coins />} label="Tokens" value={user.tokens} />
          <Card icon={<Gavel />} label="Bids placed" value={bids.length} />
          <Card icon={<Trophy />} label="Auctions won" value={wins.length} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <section className="card-surface p-6">
            <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
              <Trophy size={18} className="text-[#FBBF24]" /> Wins
            </h2>
            {wins.length === 0 ? (
              <p className="text-zinc-500 text-sm">
                No wins yet.{" "}
                <Link to="/" className="text-[#FBBF24] hover:underline">
                  Browse auctions
                </Link>
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {wins.map((w) => (
                  <li key={w.id} className="py-3 flex items-center gap-3">
                    <img
                      src={w.image_url}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{w.title}</div>
                      <div className="text-xs text-zinc-500 font-mono-nums">
                        Won at ${w.final_price?.toFixed(2)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card-surface p-6">
            <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
              <MapPin size={18} className="text-[#FBBF24]" /> Shipping address
            </h2>
            {hasAddress ? (
              <div data-testid="shipping-summary">
                <div className="text-sm leading-relaxed text-zinc-200">
                  <div className="font-medium">{shipping.full_name}</div>
                  <div>{shipping.line1}</div>
                  {shipping.line2 && <div>{shipping.line2}</div>}
                  <div>
                    {shipping.city}
                    {shipping.state && `, ${shipping.state}`} {shipping.postal_code}
                  </div>
                  <div>{shipping.country}</div>
                  {shipping.phone && (
                    <div className="text-xs text-zinc-500 mt-2">{shipping.phone}</div>
                  )}
                </div>
                <Link
                  to="/shipping"
                  className="btn-outline text-xs mt-4 inline-flex"
                  data-testid="edit-shipping-link"
                >
                  Edit
                </Link>
              </div>
            ) : (
              <>
                <p className="text-zinc-500 text-sm mb-4">
                  No address saved yet. Add one so winnings can ship instantly.
                </p>
                <Link
                  to="/shipping"
                  className="btn-bid text-xs !py-2 !px-4"
                  data-testid="add-shipping-link"
                >
                  Add shipping address
                </Link>
              </>
            )}
          </section>
        </div>

        <section className="card-surface p-6 mt-6">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
            <Gavel size={18} className="text-[#FBBF24]" /> Recent bids
          </h2>
          {bids.length === 0 ? (
            <p className="text-zinc-500 text-sm">No bids yet.</p>
          ) : (
            <ul className="divide-y divide-white/5">
              {bids.slice(0, 12).map((b) => (
                <li
                  key={b.id}
                  className="py-2.5 flex items-center justify-between text-sm"
                >
                  <Link
                    to={`/auction/${b.auction_id}`}
                    className="text-zinc-300 hover:text-white"
                  >
                    Auction #{b.auction_id.slice(0, 8)}
                  </Link>
                  <span className="font-mono-nums text-[#FBBF24] font-bold">
                    ${b.price_at_bid.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Card({ icon, label, value }) {
  return (
    <div className="card-surface p-6 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#1A1A24] border border-white/10 flex items-center justify-center text-[#FBBF24]">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-mono-nums font-bold text-3xl">{value}</div>
      </div>
    </div>
  );
}

